import {useCallback} from "react";
import {QueryKey, useQueryClient} from "react-query";
import {serverTableQueryKey} from "../../common/serverTable";

export const SURVEYS_TABLE_ID = 'system-surveys';
// Every survey (useAllSurveys), and the list a guest link is made from: whole surveys, as the QR logo needs the theme.
export const SURVEY_LIST_QUERY_KEY = 'surveyList';
export const SURVEY_SHAREABLE_QUERY_KEY = 'shareableSurveys';

export const SURVEY_GUEST_SUBMISSIONS_TABLE_ID = 'survey-guest-submissions';
export const SURVEY_INVITATIONS_TABLE_ID = 'system-survey-invitations';
export const SURVEY_GUEST_INVITATIONS_TABLE_ID = 'survey-guest-invitations';
export const SURVEY_ACTIVE_INVITATION_QUERY_KEY = 'surveyActiveInvitation';

export const SURVEY_SUBMISSIONS_TABLE_ID = 'system-survey-submissions';
// v3: the tab's columns changed twice on 2026-10-07, so column preferences stored for older sets start over.
export const SURVEY_MODULE_SUBMISSIONS_TABLE_ID = 'surveys-module-submissions-v3';
export const SURVEY_DAILY_LIST_TABLE_ID = 'surveys-daily-list';

export const SURVEY_THEMES_TABLE_ID = 'survey-feedback-themes';
export const SURVEY_TARGET_SETS_TABLE_ID = 'survey-target-sets-v2';
// The themes themselves, for filters and the AI's labels (useSurveyFeedbackThemes) - not the Themes card's figures.
export const SURVEY_FEEDBACK_THEMES_QUERY_KEY = 'surveyFeedbackThemes';

export const SURVEY_EVALUATION_QUERY_KEY = 'surveyEvaluation';
export const SURVEY_EVALUATION_SUBMISSIONS_TABLE_ID = 'survey-evaluation-submissions';
export const SURVEY_INTRODUCTION_QUERY_KEY = 'surveyIntroduction';
export const SURVEY_SERIES_QUERY_KEY = 'surveySeries';
export const SURVEY_THEMES_QUERY_KEY = 'surveyThemes';
export const SURVEY_TARGET_SETS_QUERY_KEY = 'surveyTargetSets';
export const SURVEY_BREAKDOWN_QUERY_KEY = 'surveyBreakdown';
export const SURVEY_SUMMARY_QUERY_KEY = 'surveySummary';
export const SURVEY_STATS_QUERY_KEY = 'surveyStats';
export const SURVEY_SUBMISSION_COUNTS_QUERY_KEY = 'surveySubmissionCounts';
/** A reply opened from a link, read on its own because it may sit outside the list's page and period. */
export const SURVEY_LINKED_REPLY_QUERY_KEY = 'surveyLinkedReply';

const SURVEY_REVIEW_QUERY_KEYS: QueryKey[] = [
    serverTableQueryKey(SURVEY_GUEST_SUBMISSIONS_TABLE_ID),
    serverTableQueryKey(SURVEY_SUBMISSIONS_TABLE_ID),
    serverTableQueryKey(SURVEY_MODULE_SUBMISSIONS_TABLE_ID),
    serverTableQueryKey(SURVEY_EVALUATION_SUBMISSIONS_TABLE_ID),
    // The daily list shows each answer's score, which goes with the submission.
    serverTableQueryKey(SURVEY_DAILY_LIST_TABLE_ID),
    // Resolving a reply anywhere changes the Submissions tab's Open, Unhappy guests and Resolved counts.
    [SURVEY_SUBMISSION_COUNTS_QUERY_KEY],
    [SURVEY_LINKED_REPLY_QUERY_KEY],
    [SURVEY_EVALUATION_QUERY_KEY],
    [SURVEY_INTRODUCTION_QUERY_KEY],
    [SURVEY_SERIES_QUERY_KEY],
    [SURVEY_THEMES_QUERY_KEY],
    [SURVEY_BREAKDOWN_QUERY_KEY],
    [SURVEY_STATS_QUERY_KEY],
];

// Refetched even where no screen shows them, so a picker opened after a change never flashes the old name first.
const SURVEY_LIST_QUERY_KEYS: QueryKey[] = [[SURVEY_LIST_QUERY_KEY], [SURVEY_SHAREABLE_QUERY_KEY]];

// A refetch already running when the change lands - a window-focus refetch, say - started before it and would
// bring back the old state, so it is cancelled and started again instead of joined.
const REFETCH_AFTER_CHANGE = {cancelRefetch: true};

export const useSurveyReviewRefresh = () => {
    const queryClient = useQueryClient();

    return useCallback(async () => {
        await Promise.all(SURVEY_REVIEW_QUERY_KEYS.map(key => queryClient.invalidateQueries(key, {}, REFETCH_AFTER_CHANGE)));
    }, [queryClient]);
};

export const useSurveyInvitationRefresh = () => {
    const queryClient = useQueryClient();
    return useCallback(async () => {
        await Promise.all([SURVEY_INVITATIONS_TABLE_ID, SURVEY_GUEST_INVITATIONS_TABLE_ID, SURVEY_DAILY_LIST_TABLE_ID]
            .map(tableId => queryClient.invalidateQueries(serverTableQueryKey(tableId), {}, REFETCH_AFTER_CHANGE)));
        await Promise.all([
            queryClient.invalidateQueries([SURVEY_ACTIVE_INVITATION_QUERY_KEY], {}, REFETCH_AFTER_CHANGE),
            queryClient.invalidateQueries([SURVEY_STATS_QUERY_KEY], {}, REFETCH_AFTER_CHANGE)
        ]);
    }, [queryClient]);
};

/** After a survey is created, saved, published, unpublished or deleted: the survey lists and the settings table. */
export const useSurveyChangeRefresh = () => {
    const queryClient = useQueryClient();

    return useCallback(async () => {
        await Promise.all([
            ...SURVEY_LIST_QUERY_KEYS.map(key => queryClient.invalidateQueries(key, {refetchInactive: true}, REFETCH_AFTER_CHANGE)),
            queryClient.invalidateQueries(serverTableQueryKey(SURVEYS_TABLE_ID), {}, REFETCH_AFTER_CHANGE),
        ]);
    }, [queryClient]);
};
